import { Injectable } from '@angular/core';
import { AccessService } from '../access/access.service';
import { CommonHttpService } from '../common-http/common-http.service';
import { DataService } from '../data/data.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, skip, takeUntil } from 'rxjs/operators';
import { ClientConfig } from '@app-core/models/core.model';
import { CoachingConfigResolver } from 'src/app/resolvers/coaching-config/coaching-config.resolver';
import { DurationRangeService } from '../duration-range/duration-range.service';
import { UserRoleManageService } from '@app-user-management/service/user-role-manage.service';
import { UserFleetInfo } from '@app-user-management/models/users.model';

@Injectable({
  providedIn: 'root',
})
export class StartupService {
  private fleetSdkConfig$ = new BehaviorSubject<any>({});
  private fleetDetails$ = new BehaviorSubject<any>({});
  private clientConfig$ = new BehaviorSubject<ClientConfig>({});
  public destroy$: Subject<void> = new Subject<void>();
  constructor(
    private accessService: AccessService,
    private commonHttpService: CommonHttpService,
    private dataService: DataService,
    private coachingConfigResolver: CoachingConfigResolver,
    private userRoleManageService: UserRoleManageService,
    private durationRangeService: DurationRangeService
  ) {
    this.dataService._currentFleet.pipe(skip(1), takeUntil(this.destroy$)).subscribe(async (value: string) => {
      if (value) {
        await this.loadData();
      }
    });
  }

  get fleetSdkConfig() {
    return this.fleetSdkConfig$;
  }

  get fleetDetails() {
    return this.fleetDetails$;
  }

  get clientConfig() {
    return this.clientConfig$;
  }

  private loadFleetSdkConfig() {
    return this.commonHttpService.getFleetSdkConfigurations().toPromise();
  }

  private loadFleetDetails() {
    return this.dataService.getFleetDetails().toPromise();
  }

  private loadClientConfig() {
    return this.durationRangeService.getClientConfig().toPromise();
  }

  private loadFleetEvents() {
    return this.commonHttpService.getFleetEvents().toPromise();
  }

  private loadFleetCustomEventsConfig() {
    return this.commonHttpService.getCustomEvents().toPromise();
  }

  private loadCoachingConfig() {
    return this.coachingConfigResolver.resolve().toPromise();
  }

  private async loadData() {
    if (!this.accessService.isUserAuthenticated()) {
      return;
    }

    try {
      // Use Promise.allSettled only for essential setup methods
      const results = await Promise.allSettled([
        this.loadFleetDetails(),
        this.loadFleetSdkConfig(),
        this.loadClientConfig(),
        this.loadFleetEvents(),
        this.loadFleetCustomEventsConfig(),
      ]);
      /// Extract successful values
      const fleetDetails = results[0].status === 'fulfilled' ? results[0].value : null;
      const sdkConfig = results[1].status === 'fulfilled' ? results[1].value : null;
      const clientConfig = results[2].status === 'fulfilled' ? results[2].value : null;

      /// Update observables with successful values
      this.fleetDetails$.next(fleetDetails);
      this.fleetSdkConfig$.next(sdkConfig);
      this.clientConfig$.next(clientConfig?.data || {});
    } catch (error) {
      console.error('startup error', error.message);
    }

    try {
      // Fire-and-forget calls (executed after the critical setup, no waiting)
      // this.loadFleetCustomEventsConfig();

      // Wait for loadCoachingConfig() and then set the storage value
      this.loadCoachingConfig();

      //  this changes is added prevent 504 access token error when user login to portal who has (N) number of tags
      const userFleetsWithTags = await this.getUserFleetsWithTags().toPromise();
      const currentFleet = this.dataService._currentFleet.getValue();
      const fleetData = userFleetsWithTags.find((fleet) => fleet.fleetId === currentFleet);
      const userTags = fleetData?.userTags || [];
      const loginInfo = this.accessService.getLoginInfo();
      // OR attach per fleet (recommended if multiple fleets)
      const currentFleetData = loginInfo.fleets.find((fleet) => fleet.fleetId === currentFleet);
      if (currentFleetData) currentFleetData.userTags = userTags;

      // save back to localStorage (same key used in your app)
      this.accessService.setLoginInfo(loginInfo);
    } catch (error) {}
  }

  public load() {
    return this.loadData();
  }

  public clearSubscriptions() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * **NOTE: Do not** try to access roles from
   * Observable returned by this fuction.
   * Since, SSO user will not have role present.
   *
   * This function gets user details for Cognito / SSO user
   * and extracts fleets from the details and returns it as an Observable
   *
   * @returns Fleets of user with fleetId and userTags only.
   *
   * @inner Strictly internal!
   */
  private getUserFleetsWithTags(): Observable<Omit<UserFleetInfo, 'role'>[]> {
    const loginInfo = this.accessService.getLoginInfo();
    if (loginInfo.userId) {
      return this.dataService.getUserDetails(loginInfo.userId).pipe(map((v) => v?.data?.fleets || []));
    } else if (loginInfo.userHash) {
      return this.userRoleManageService.getSSOUserDetails(loginInfo.userHash).pipe(map((v) => v?.fleets || []));
    }
  }
}
